import React from "react";
import styled from "styled-components";
import {
    Button,
    Checkbox,
    CloseIcon,
    Dialog,
    Dropdown,
    Flex,
    InputLabel,
} from "@fluentui/react-northstar";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { Tab, TabList } from "@fluentui/react-components";
import {
    EXPORT_AGENCES,
    EXPORT_DEPARTEMENTS,
    EXPORT_DETAIL_GROUPS,
    EXPORT_FORMATS,
    EXPORT_MANAGERS,
    EXPORT_SYNTHESE_OPTIONS,
} from "../../constants";
import { EXPORT_TABS } from "../../hooks/useExportDialog";
import { DEVICE_MAX } from "../../../../constants/responsive";

const toItems = (list) => list.map((v) => ({ key: v, header: v }));

/** Multi-sélection avec recherche (Département / Agence / Manager / Collaborateurs) */
const MultiSelect = ({ label, items, value, onChange, placeholder }) => (
    <Field>
        <InputLabel content={label} />
        <Dropdown
            multiple
            search
            fluid
            items={items}
            value={items.filter((i) => value.includes(i.key))}
            onChange={(_, data) =>
                onChange((data?.value || []).map((i) => i.key))
            }
            placeholder={value.length ? "Ajouter…" : placeholder}
            noResultsMessage="Aucun résultat"
            positionFixed
        />
    </Field>
);

const ExportDialog = ({ logic }) => {
    const { dialog, tab, periode, format, perimetre, colonnes, exportData } =
        logic;

    return (
        <Dialog
            open={dialog.open}
            header="Exporter les données"
            headerAction={{ icon: <CloseIcon />, onClick: dialog.close }}
            onCancel={dialog.close}
            confirmButton={{
                content: "Exporter les données",
                flat: true,
                onClick: exportData,
            }}
            styles={{ width: "660px", maxWidth: "100%" }}
            content={
                <Flex column gap="gap.medium">
                    {/* ---- Période et format ---- */}
                    <BlockTitle>Période et format</BlockTitle>
                    <Row3>
                        <Field>
                            <InputLabel content="À partir du" />
                            <DatePicker
                                strings={periode.localizedStrings}
                                formatDate={periode.formatDate}
                                value={periode.from}
                                onSelectDate={periode.setFrom}
                                maxDate={periode.to}
                                placeholder="jj/mm/aaaa"
                                allowTextInput
                            />
                        </Field>
                        <Field>
                            <InputLabel content="Jusqu'au" />
                            <DatePicker
                                strings={periode.localizedStrings}
                                formatDate={periode.formatDate}
                                value={periode.to}
                                onSelectDate={periode.setTo}
                                minDate={periode.from}
                                placeholder="jj/mm/aaaa"
                                allowTextInput
                            />
                        </Field>
                        <Field>
                            <InputLabel content="Format" />
                            <Dropdown
                                fluid
                                checkable
                                items={EXPORT_FORMATS}
                                value={format.value}
                                onChange={(_, data) =>
                                    data?.value && format.set(data.value)
                                }
                            />
                        </Field>
                    </Row3>

                    {/* ---- Périmètre ---- */}
                    <BlockTitle>Périmètre</BlockTitle>
                    <Row3>
                        <MultiSelect
                            label="Département"
                            items={toItems(EXPORT_DEPARTEMENTS)}
                            value={perimetre.departements}
                            onChange={perimetre.setDepartements}
                            placeholder="Tous"
                        />
                        <MultiSelect
                            label="Agence"
                            items={toItems(EXPORT_AGENCES)}
                            value={perimetre.agences}
                            onChange={perimetre.setAgences}
                            placeholder="Toutes"
                        />
                        <MultiSelect
                            label="Manager"
                            items={toItems(EXPORT_MANAGERS)}
                            value={perimetre.managers}
                            onChange={perimetre.setManagers}
                            placeholder="Tous"
                        />
                    </Row3>

                    {/* ---- Onglets ---- */}
                    <TabList
                        selectedValue={tab.value}
                        onTabSelect={(_, data) => tab.set(data.value)}
                    >
                        <Tab value={EXPORT_TABS.synthese}>Synthèse</Tab>
                        <Tab value={EXPORT_TABS.detail}>
                            Détail collaborateurs
                        </Tab>
                    </TabList>

                    {tab.value === EXPORT_TABS.synthese && (
                        <Checks>
                            {EXPORT_SYNTHESE_OPTIONS.map((opt) => (
                                <Checkbox
                                    key={opt}
                                    label={opt}
                                    checked={!!colonnes.synthese[opt]}
                                    onChange={() =>
                                        colonnes.toggleSynthese(opt)
                                    }
                                />
                            ))}
                        </Checks>
                    )}

                    {tab.value === EXPORT_TABS.detail && (
                        <>
                            <MultiSelect
                                label="Collaborateurs concernés"
                                items={perimetre.collaborateursDisponibles}
                                value={perimetre.collaborateurs}
                                onChange={perimetre.setCollaborateurs}
                                placeholder="Tous les collaborateurs du périmètre"
                            />
                            <Checks>
                                {EXPORT_DETAIL_GROUPS.map((group) => (
                                    <React.Fragment key={group.key}>
                                        <GroupTitle>
                                            {group.label}
                                            {group.toggleable && (
                                                <Button
                                                    text
                                                    size="small"
                                                    content={
                                                        colonnes.isGroupAllChecked(
                                                            group
                                                        )
                                                            ? "Tout désélectionner"
                                                            : "Tout sélectionner"
                                                    }
                                                    onClick={() =>
                                                        colonnes.toggleGroup(
                                                            group
                                                        )
                                                    }
                                                    styles={{
                                                        color: "var(--prh-violet)",
                                                        minWidth: 0,
                                                        padding: 0,
                                                    }}
                                                />
                                            )}
                                        </GroupTitle>
                                        {group.options.map((opt) => (
                                            <Checkbox
                                                key={opt.key}
                                                label={opt.label}
                                                checked={
                                                    !!colonnes.detail[opt.key]
                                                }
                                                onChange={() =>
                                                    colonnes.toggleDetail(
                                                        opt.key
                                                    )
                                                }
                                            />
                                        ))}
                                    </React.Fragment>
                                ))}
                            </Checks>
                        </>
                    )}
                </Flex>
            }
        />
    );
};

export default ExportDialog;

const BlockTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: var(--prh-ink);
`;

const Row3 = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
    @media ${DEVICE_MAX.tablet} {
        grid-template-columns: 1fr;
    }
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
`;

const Checks = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px 20px;
    padding: 4px 0;
    @media ${DEVICE_MAX.tablet} {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const GroupTitle = styled.div`
    grid-column: 1 / -1;
    font-size: 13px;
    font-weight: 600;
    color: var(--prh-muted);
    margin-top: 6px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
`;
